import {
  AddShoppingCartOutlined,
  ArticleOutlined,
  CompareArrowsOutlined,
  CompareOutlined,
  DifferenceOutlined,
  EditNoteOutlined,
  GppMaybeOutlined,
  HelpCenterOutlined,
  InsertDriveFileOutlined,
  Inventory2Outlined,
  PageviewOutlined,
  RequestQuoteOutlined,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { Restricted } from '@procureai/permissions-ui';
import { ExtendedList } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChatRequestOption } from '../ChatRequestOption';
import { IntakeColumn } from '../IntakeColumn';
import { SvbrgAgentId, SvbrgChatSessionVariant } from '@/generated/gql/graphql';
import { FeatureFlag, I18nNamespace } from '~/common/types';
import { RouteId } from '~/routes/RouteId';

interface ToolSelectionProps {
  onClick: (variant: SvbrgChatSessionVariant) => void;
  onClickNewThread: (agentId: SvbrgAgentId, initialMessage?: string) => Promise<void>;
}

export const ToolSelection = ({ onClick, onClickNewThread }: ToolSelectionProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeCreate' });
  const navigate = useNavigate();

  return (
    <Grid
      container
      spacing={6}
      sx={{
        width: '100%',
        overflowY: 'auto',
      }}
    >
      {/* Request & Process Column */}
      <Restricted
        needs={{
          permission: {
            any: [
              Permission.IntakeCreateQuote,
              Permission.IntakeCreateFreeText,
              Permission.IntakeCreateForm,
              Permission.IntakeCreateFormExtended,
              Permission.SearchView,
              Permission.IntakeAgentCatalogueSearch,
              Permission.SearchIndexOrdersView,
              Permission.IntakeAgentSupplierIdentification,
              Permission.IntakeAgentSupplierSearch,
            ],
          },
        }}
      >
        <IntakeColumn title={t('request.title')}>
          <ExtendedList>
            <Restricted needs={{ permission: Permission.IntakeCreateQuote }}>
              <ChatRequestOption
                title={t('request.optionQuote.title')}
                description={t('request.optionQuote.description')}
                Icon={RequestQuoteOutlined}
                onClick={() => onClick(SvbrgChatSessionVariant.REQUEST_QUOTE)}
              />
            </Restricted>
            {/* POC not production ready */}
            <Restricted needs={{ permission: Permission.IntakeCreateFreeText }}>
              <ChatRequestOption
                title={t('request.optionFreeText.title')}
                description={t('request.optionFreeText.description')}
                Icon={InsertDriveFileOutlined}
                onClick={() => onClick(SvbrgChatSessionVariant.REQUEST_CHAT)}
              />
            </Restricted>
            {/* Uses restricted to show either or but not both */}
            <Restricted needs={{ permission: Permission.IntakeCreateForm }}>
              <ChatRequestOption
                title={t('request.optionForm.title')}
                description={t('request.optionForm.description')}
                Icon={ArticleOutlined}
                onClick={() => onClick(SvbrgChatSessionVariant.REQUEST_FORM)}
              />
            </Restricted>
            <Restricted needs={{ permission: Permission.IntakeCreateFormExtended }}>
              <ChatRequestOption
                title={t('request.optionComplexForm.title')}
                description={t('request.optionComplexForm.description')}
                Icon={ArticleOutlined}
                onClick={() => onClick(SvbrgChatSessionVariant.REQUEST_FORM_COMPLEX)}
              />
            </Restricted>

            {/* Feature Flagged flow for DLR PoC: Statement of Work generation */}
            <Restricted needs={{ feature: FeatureFlag.INTAKE_FLOW_SOW }}>
              <ChatRequestOption
                title={t('request.openStatementOfWork.title')}
                description={t('request.openStatementOfWork.description')}
                Icon={EditNoteOutlined}
                onClick={() => onClick(SvbrgChatSessionVariant.REQUEST_DLR)}
              />
            </Restricted>

            <Restricted needs={{ permission: Permission.SearchView }}>
              <ChatRequestOption
                title={t('search.optionAiSearch.title')}
                description={t('search.optionAiSearch.description')}
                Icon={PageviewOutlined}
                onClick={() => {
                  navigate(RouteId.search);
                }}
              />
            </Restricted>

            <Restricted needs={{ permission: Permission.IntakeAgentCatalogueSearch }}>
              <ChatRequestOption
                title={t('search.optionCatalogue.title')}
                description={t('search.optionCatalogue.description')}
                Icon={AddShoppingCartOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.catalogueSearchAgent)}
              />
            </Restricted>

            <Restricted needs={{ permission: Permission.SearchIndexOrdersView }}>
              <ChatRequestOption
                title={t('search.optionPurchaseOrders.title')}
                description={t('search.optionPurchaseOrders.description')}
                Icon={PageviewOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.procurementItemsAgent)}
              />
            </Restricted>

            <Restricted needs={{ feature: FeatureFlag.ENABLE_MATERIAL_SEARCH }}>
              <ChatRequestOption
                title={t('search.optionMaterialSearch.title')}
                description={t('search.optionMaterialSearch.description')}
                Icon={Inventory2Outlined}
                onClick={() =>
                  onClickNewThread(SvbrgAgentId.procurementItemsAgent, t('search.optionMaterialSearch.starterMessage'))
                }
              />
            </Restricted>

            <Restricted needs={{ permission: Permission.IntakeAgentSupplierIdentification }}>
              <ChatRequestOption
                title={t('search.optionSourcerAgent.title')}
                description={t('search.optionSourcerAgent.description')}
                Icon={PageviewOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.sourcerAgent)}
              />
            </Restricted>
            <Restricted needs={{ permission: Permission.IntakeAgentSupplierSearch }}>
              <ChatRequestOption
                title={t('search.optionSupplierSearch.title')}
                description={t('search.optionSupplierSearch.description')}
                Icon={PageviewOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.companyGraphSupplierSearchAgent)}
              />
            </Restricted>
          </ExtendedList>
        </IntakeColumn>
      </Restricted>
      {/* Compare & Choose Column */}
      <Restricted
        needs={{
          permission: {
            any: [Permission.IntakeAgentContractComparison, Permission.IntakeAgentQuoteComparison],
          },
        }}
      >
        <IntakeColumn title={t('compare.title')}>
          <ExtendedList>
            <Restricted needs={{ permission: Permission.IntakeAgentContractComparison }}>
              <ChatRequestOption
                title={t('compare.optionContract.title')}
                description={t('compare.optionContract.description')}
                Icon={CompareOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.contractComparisonAgent)}
              />
            </Restricted>
            <Restricted needs={{ permission: Permission.IntakeAgentQuoteComparison }}>
              <ChatRequestOption
                title={t('compare.optionQuote.title')}
                description={t('compare.optionQuote.description')}
                Icon={DifferenceOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.quoteComparisonAgent)}
              />
            </Restricted>
          </ExtendedList>
        </IntakeColumn>
      </Restricted>

      {/* Stay Compliant Column */}
      <Restricted needs={{ permission: Permission.IntakeAgentProcurementKnowledge }}>
        <IntakeColumn title={t('compliance.title')}>
          <ExtendedList>
            <Restricted needs={{ permission: Permission.IntakeAgentProcurementKnowledge }}>
              <ChatRequestOption
                title={t('compliance.optionQA.title')}
                description={t('compliance.optionQA.description')}
                Icon={HelpCenterOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.procurementKnowledgeAgent)}
              />
            </Restricted>
            <Restricted needs={{ permission: Permission.IntakeAgentPolicyChecker }}>
              <ChatRequestOption
                title={t('compliance.optionQuoteCheck.title')}
                description={t('compliance.optionQuoteCheck.description')}
                Icon={GppMaybeOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.policyCheckerAgent)}
              />
              <ChatRequestOption
                title={t('compliance.optionContractCheck.title')}
                description={t('compliance.optionContractCheck.description')}
                Icon={GppMaybeOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.policyCheckerAgent)}
              />
              <ChatRequestOption
                title={t('compliance.optionTermsCheck.title')}
                description={t('compliance.optionTermsCheck.description')}
                Icon={CompareArrowsOutlined}
                onClick={() => onClickNewThread(SvbrgAgentId.policyCheckerAgent)}
              />
            </Restricted>
          </ExtendedList>
        </IntakeColumn>
      </Restricted>
    </Grid>
  );
};
